// Create Vendor Return — multi-select passports with barcode scan,
// per-item return reasons (controlled dropdown + notes for OTHER).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorToMessage } from "@/lib/apiClient";
import PassportPicker from "@/components/PassportPicker";

// Only these statuses may be added to a NEW Vendor Return.
const ELIGIBLE = new Set([
  "QC_PASSED", "AVAILABLE", "QC_PENDING", "VENDOR_CASE",
  "READY_FOR_SALE", "PENDING_BATTERY_DETAILS", "REPAIR", "SERVICE",
  "WARRANTY_INSPECTION", "RETURNED_AFTER_REPAIR",
]);

export default function NewVendorReturnPage() {
  const nav = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [returnDate, setReturnDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [courier, setCourier] = useState("");
  const [tracking, setTracking] = useState("");
  const [items, setItems] = useState([]);              // {passport_id, passport_uid, item_type, subtitle, return_reason, reason_notes}
  const [reasons, setReasons] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      const [{ data: vs }, { data: ref }] = await Promise.all([
        api.get("/vendors", { params: { page_size: 200 } }),
        api.get("/vendor-returns/reference-data"),
      ]);
      setVendors(vs.items || []);
      setReasons(ref.return_reasons || []);
    })();
  }, []);

  const filterPassport = (it) => {
    if (!ELIGIBLE.has(it.meta?.status)) return false;
    if (items.some((x) => x.passport_id === it.id)) return false;
    return true;
  };

  // When a passport is added, resolve its ORIGINATING vendor from the existing
  // Digital Passport API (header.vendor_name) and match it to the loaded vendor
  // list. Vendor names are unique per company, so name -> id is safe. No backend
  // change: this reuses GET /api/passports/{id}/digital-passport.
  const addItem = (p) => {
    setItems((cur) => [
      ...cur,
      { passport_id: p.id, passport_uid: p.passport_uid,
        item_type: p.item_type, subtitle: p.title,
        status: p.status, return_reason: "FAILED_QC", reason_notes: "",
        vendor_name: null, vendor_loading: true }
    ]);
    (async () => {
      let vname = null;
      try {
        const { data } = await api.get(`/passports/${p.id}/digital-passport`, { params: { activity_limit: 1 } });
        vname = data?.header?.vendor_name || null;
      } catch { /* leave vendor undetermined — field stays selectable, no invented vendor */ }
      setItems((cur) => cur.map((x) => (
        x.passport_id === p.id ? { ...x, vendor_name: vname, vendor_loading: false } : x
      )));
    })();
  };

  const removeItem = (id) => setItems((cur) => cur.filter((x) => x.passport_id !== id));
  const patchItem  = (id, patch) => setItems((cur) => cur.map((x) => x.passport_id === id ? { ...x, ...patch } : x));

  // Resilient vendor-name -> id match against the loaded vendor list
  // (trimmed + case-insensitive; vendor names are unique per company).
  const matchVendorId = useCallback((name) => {
    if (!name) return null;
    const key = String(name).trim().toLowerCase();
    const v = vendors.find((x) => String(x.name || "").trim().toLowerCase() === key);
    return v ? v.id : null;
  }, [vendors]);

  // Resolve each item's vendor id from the CURRENT vendor list. Deriving this
  // (instead of storing it at add-time) avoids load-order / stale-closure bugs
  // where vendors finished loading only after the passport was added.
  const resolvedVendorIds = useMemo(
    () => items.map((i) => matchVendorId(i.vendor_name)),
    [items, matchVendorId]
  );
  const distinctVendorIds = useMemo(
    () => [...new Set(resolvedVendorIds.filter(Boolean))],
    [resolvedVendorIds]
  );
  const conflictVendorNames = useMemo(
    () => [...new Set(items.filter((i) => i.vendor_name).map((i) => i.vendor_name))],
    [items]
  );
  const vendorConflict = distinctVendorIds.length > 1;

  // Auto-populate the Vendor field from the passports' originating vendor when
  // they all agree on one vendor. If none can be resolved, leave it selectable.
  useEffect(() => {
    if (distinctVendorIds.length === 1 && vendorId !== distinctVendorIds[0]) {
      setVendorId(distinctVendorIds[0]);
    }
  }, [distinctVendorIds]);  

  const canSubmit = useMemo(() => {
    if (!vendorId || items.length === 0) return false;
    if (vendorConflict) return false;
    for (const it of items) {
      if (!it.return_reason) return false;
      if (it.return_reason === "OTHER" && !it.reason_notes.trim()) return false;
    }
    return true;
  }, [vendorId, items, vendorConflict]);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const body = {
        vendor_id: vendorId, return_date: returnDate,
        remarks: remarks || null,
        courier_name: courier || null, tracking_no: tracking || null,
        items: items.map((it) => ({
          passport_id: it.passport_id,
          return_reason: it.return_reason,
          reason_notes: it.reason_notes || null,
        })),
      };
      const { data } = await api.post("/vendor-returns", body);
      nav(`/admin/vendor-returns/${data.id}`);
    } catch (e) { setError(errorToMessage(e, "Could not create Vendor Return")); }
    finally { setBusy(false); }
  };

  return (
    <section className="p-6" data-testid="vr-new-page">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-slate-900">New Vendor Return</h1>
        <p className="text-sm text-slate-500">Select passports and assign a reason to each.</p>
      </header>

      {error && <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      {vendorConflict && (
        <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="vr-vendor-conflict">
          The added passports belong to different vendors ({conflictVendorNames.join(", ")}).
          A vendor return can only contain items from a single vendor. Remove the conflicting passport(s) to continue.
        </div>
      )}

      {/* Header form */}
      <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-4">
        <label className="text-xs font-medium text-slate-700">Vendor
          <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                  data-testid="vr-vendor" required
                  className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm">
            <option value="">— Select —</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </label>
        <label className="text-xs font-medium text-slate-700">Return Date
          <input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                 data-testid="vr-date"
                 className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs font-medium text-slate-700">Courier (optional)
          <input value={courier} onChange={(e) => setCourier(e.target.value)}
                 data-testid="vr-courier"
                 className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs font-medium text-slate-700">Tracking No (optional)
          <input value={tracking} onChange={(e) => setTracking(e.target.value)}
                 data-testid="vr-tracking"
                 className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
        </label>
      </div>

      {/* Add items */}
      <div className="mb-3 rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500">
          Add passports (scan barcode / QR or type)
        </div>
        <PassportPicker onPick={addItem} filterFn={filterPassport}
                         testId="vr-picker" placeholder="Scan or type Passport ID (RP…) or serial" />
      </div>

      {/* Items table */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Passport</th>
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-left">Current Status</th>
              <th className="px-3 py-2 text-left">Return Reason</th>
              <th className="px-3 py-2 text-left">Notes</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-500">
                No passports added yet. Scan or search above.
              </td></tr>
            )}
            {items.map((it, i) => (
              <tr key={it.passport_id} className="border-t border-slate-100" data-testid={`vr-item-${it.passport_uid}`}>
                <td className="px-3 py-2 font-mono">
                  {it.passport_uid}
                  {it.item_type && it.item_type !== "laptop" && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 border border-indigo-200 px-1.5 py-0 text-[9px] font-semibold uppercase text-indigo-800">
                      {it.item_type}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{it.subtitle}</td>
                <td className="px-3 py-2 text-xs" data-testid={`vr-item-vendor-${it.passport_uid}`}>
                  {it.vendor_loading ? (
                    <span className="text-slate-400">Resolving…</span>
                  ) : it.vendor_name ? (
                    <span className={vendorConflict && resolvedVendorIds[i] !== vendorId ? "font-medium text-amber-700" : "text-slate-700"}>
                      {it.vendor_name}{!resolvedVendorIds[i] && <span className="text-slate-400"> (not in list)</span>}
                    </span>
                  ) : (
                    <span className="text-slate-400">Unknown</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{it.status}</td>
                <td className="px-3 py-2">
                  <select value={it.return_reason}
                          onChange={(e) => patchItem(it.passport_id, { return_reason: e.target.value })}
                          className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                          data-testid={`vr-reason-${it.passport_uid}`}>
                    {reasons.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input value={it.reason_notes}
                         onChange={(e) => patchItem(it.passport_id, { reason_notes: e.target.value })}
                         placeholder={it.return_reason === "OTHER" ? "Required" : "Optional"}
                         className={`w-full rounded-md border px-2 py-1 text-xs ${
                           it.return_reason === "OTHER" && !it.reason_notes.trim()
                             ? "border-rose-300 bg-rose-50" : "border-slate-300"}`}
                         data-testid={`vr-notes-${it.passport_uid}`} />
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => removeItem(it.passport_id)}
                          className="text-xs text-rose-600 hover:underline"
                          data-testid={`vr-remove-${it.passport_uid}`}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <label className="text-xs font-medium text-slate-700 block">Remarks (optional)
          <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    data-testid="vr-remarks" />
        </label>
      </div>

      <div className="mt-5 flex items-center justify-end gap-3">
        <button onClick={() => nav("/admin/vendor-returns")}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
        <button onClick={submit} disabled={!canSubmit || busy}
                data-testid="vr-submit"
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
          {busy ? "Creating…" : `Create Vendor Return (${items.length} item${items.length === 1 ? "" : "s"})`}
        </button>
      </div>
    </section>
  );
}
